import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableSortLabel,
  MenuItem, Tooltip, Collapse, Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ForumIcon from '@mui/icons-material/Forum';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { communicationsAPI } from '../services/api';
import FileDropField from '../components/common/FileDropField';

const DIRECTION_OPTIONS = [
  { value: 'sent', label: 'Enviada' },
  { value: 'received', label: 'Recibida' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'oferta', label: 'Oferta' },
  { value: 'tecnico', label: 'Tecnico' },
  { value: 'logistica', label: 'Logistica' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'facturacion', label: 'Facturacion' },
  { value: 'incidencia', label: 'Incidencia' },
];

const CATEGORY_COLORS = {
  general: 'default',
  oferta: 'primary',
  tecnico: 'info',
  logistica: 'warning',
  comercial: 'secondary',
  facturacion: 'success',
  incidencia: 'error',
};

const PENDING_STATUS_OPTIONS = [
  { value: 'open', label: 'Abierto' },
  { value: 'in_progress', label: 'En curso' },
  { value: 'resolved', label: 'Resuelto' },
];

const PENDING_STATUS_COLORS = {
  open: 'error',
  in_progress: 'warning',
  resolved: 'success',
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
];

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'warning',
  high: 'error',
};

const EMPTY_FORM = {
  entry_type: 'communication',
  direction: 'sent',
  subject: '',
  body: '',
  contact_name: '',
  communication_date: new Date().toISOString().slice(0, 16),
  category: 'general',
  pending_status: 'open',
  priority: 'medium',
  due_date: '',
  resolution: '',
  assigned_to: '',
  links: [],
};

export default function Communications({ projectId }) {
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Sub-tabs: 0 = Comunicaciones, 1 = Temas pendientes
  const [subTab, setSubTab] = useState(0);

  // Sort & group
  const [sortField, setSortField] = useState('communication_date');
  const [sortDir, setSortDir] = useState('desc');
  const [groupBy, setGroupBy] = useState('none');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterPendingStatus, setFilterPendingStatus] = useState('');

  useEffect(() => {
    if (projectId) loadComms();
  }, [projectId]);

  const loadComms = async () => {
    try {
      setLoading(true);
      const resp = await communicationsAPI.list(projectId);
      setComms(resp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading communications:', err);
      setError('Error al cargar el seguimiento.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Filter by entry_type based on current subTab
  const activeEntryType = subTab === 0 ? 'communication' : 'pending';
  const commsByType = useMemo(
    () => comms.filter((c) => (c.entry_type || 'communication') === activeEntryType),
    [comms, activeEntryType]
  );

  const filtered = useMemo(() => {
    let result = [...commsByType];
    if (filterCategory) result = result.filter((c) => c.category === filterCategory);
    if (filterDirection) result = result.filter((c) => c.direction === filterDirection);
    if (activeEntryType === 'pending' && filterPendingStatus) {
      result = result.filter((c) => c.pending_status === filterPendingStatus);
    }
    return result;
  }, [commsByType, filterCategory, filterDirection, filterPendingStatus, activeEntryType]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortField] || '';
      let vb = b[sortField] || '';
      if (sortField === 'communication_date' || sortField === 'due_date') {
        va = new Date(va).getTime() || 0;
        vb = new Date(vb).getTime() || 0;
      } else {
        va = String(va).toLowerCase();
        vb = String(vb).toLowerCase();
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [filtered, sortField, sortDir]);

  const grouped = useMemo(() => {
    if (groupBy === 'none') return { '': sorted };
    const groups = {};
    sorted.forEach((c) => {
      let key = '';
      if (groupBy === 'category') key = c.category || 'Sin categoria';
      else if (groupBy === 'direction') key = c.direction === 'sent' ? 'Enviadas' : 'Recibidas';
      else if (groupBy === 'status') key = PENDING_STATUS_OPTIONS.find((o) => o.value === c.pending_status)?.label || 'Sin estado';
      else if (groupBy === 'month') {
        const d = new Date(c.communication_date);
        key = isNaN(d.getTime()) ? 'Sin fecha' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    });
    return groups;
  }, [sorted, groupBy]);

  // Counts for subtabs
  const commCount = useMemo(() => comms.filter((c) => (c.entry_type || 'communication') === 'communication').length, [comms]);
  const pendingCount = useMemo(() => comms.filter((c) => c.entry_type === 'pending').length, [comms]);
  const openPendingCount = useMemo(() => comms.filter((c) => c.entry_type === 'pending' && c.pending_status !== 'resolved').length, [comms]);

  const handleOpen = (comm = null) => {
    if (comm) {
      setEditingId(comm.id);
      setForm({
        entry_type: comm.entry_type || 'communication',
        direction: comm.direction || 'sent',
        subject: comm.subject || '',
        body: comm.body || '',
        contact_name: comm.contact_name || '',
        communication_date: comm.communication_date ? comm.communication_date.slice(0, 16) : '',
        category: comm.category || 'general',
        pending_status: comm.pending_status || 'open',
        priority: comm.priority || 'medium',
        due_date: comm.due_date ? comm.due_date.slice(0, 10) : '',
        resolution: comm.resolution || '',
        assigned_to: comm.assigned_to || '',
        links: (comm.links || []).map((l) => ({ label: l.label, url: l.url })),
      });
    } else {
      setEditingId(null);
      setForm({
        ...EMPTY_FORM,
        entry_type: activeEntryType,
        communication_date: new Date().toISOString().slice(0, 16),
        links: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.subject) return;
    try {
      setSaving(true);
      const payload = {
        entry_type: form.entry_type || 'communication',
        direction: form.direction,
        subject: form.subject,
        body: form.body || '',
        contact_name: form.contact_name || '',
        communication_date: form.communication_date || new Date().toISOString(),
        category: form.category || 'general',
        pending_status: form.entry_type === 'pending' ? (form.pending_status || 'open') : null,
        priority: form.entry_type === 'pending' ? (form.priority || null) : null,
        due_date: form.entry_type === 'pending' && form.due_date ? form.due_date : null,
        resolution: form.entry_type === 'pending' ? (form.resolution || '') : null,
        assigned_to: form.entry_type === 'pending' ? (form.assigned_to || '') : null,
        links: form.links.filter((l) => l.url),
      };
      if (editingId) {
        await communicationsAPI.update(editingId, payload);
      } else {
        await communicationsAPI.create(projectId, payload);
      }
      setDialogOpen(false);
      await loadComms();
    } catch (err) {
      console.error('Error saving:', err);
      setError('Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await communicationsAPI.delete(id);
      setDeleteConfirm(null);
      await loadComms();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  // Link management in dialog
  const addLink = () => setForm({ ...form, links: [...form.links, { label: '', url: '' }] });
  const updateLink = (idx, field, value) => {
    const updated = [...form.links];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, links: updated });
  };
  const removeLink = (idx) => setForm({ ...form, links: form.links.filter((_, i) => i !== idx) });

  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return ''; }
  };

  const fmtDateOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };

  const getPendingStatusIcon = (status) => {
    if (status === 'resolved') return <CheckCircleIcon fontSize="small" color="success" />;
    if (status === 'in_progress') return <PlayCircleOutlineIcon fontSize="small" color="warning" />;
    return <HourglassEmptyIcon fontSize="small" color="error" />;
  };

  const isOverdue = (comm) => {
    if (!comm.due_date || comm.pending_status === 'resolved') return false;
    return new Date(comm.due_date).getTime() < new Date().setHours(0, 0, 0, 0);
  };

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const isPendingTab = activeEntryType === 'pending';

  const renderRow = (comm) => (
    <React.Fragment key={comm.id}>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          ...(isOverdue(comm) ? { backgroundColor: '#ffebee' } : {}),
        }}
        onClick={() => setExpandedId(expandedId === comm.id ? null : comm.id)}
      >
        <TableCell sx={{ width: 40 }}>
          {isPendingTab
            ? <Tooltip title={PENDING_STATUS_OPTIONS.find((o) => o.value === comm.pending_status)?.label || 'Abierto'}>{getPendingStatusIcon(comm.pending_status)}</Tooltip>
            : (comm.direction === 'sent'
                ? <Tooltip title="Enviada"><SendIcon fontSize="small" color="primary" /></Tooltip>
                : <Tooltip title="Recibida"><CallReceivedIcon fontSize="small" color="success" /></Tooltip>)
          }
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>{comm.subject}</TableCell>
        <TableCell>{comm.contact_name || (isPendingTab ? comm.assigned_to : '')}</TableCell>
        <TableCell>
          {comm.category && (
            <Chip
              size="small"
              label={CATEGORY_OPTIONS.find((c) => c.value === comm.category)?.label || comm.category}
              color={CATEGORY_COLORS[comm.category] || 'default'}
              variant="outlined"
            />
          )}
        </TableCell>
        {isPendingTab && (
          <TableCell>
            {comm.priority && (
              <Chip
                size="small"
                label={PRIORITY_OPTIONS.find((o) => o.value === comm.priority)?.label || comm.priority}
                color={PRIORITY_COLORS[comm.priority] || 'default'}
              />
            )}
          </TableCell>
        )}
        {isPendingTab && (
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: isOverdue(comm) ? 'bold' : 'normal', color: isOverdue(comm) ? 'error.main' : 'inherit' }}>
              {fmtDateOnly(comm.due_date) || '-'}
            </Typography>
          </TableCell>
        )}
        <TableCell>{fmtDate(comm.communication_date)}</TableCell>
        <TableCell>
          {(comm.links?.length || 0) > 0 && (
            <Chip size="small" icon={<LinkIcon />} label={comm.links.length} variant="outlined" />
          )}
        </TableCell>
        <TableCell align="right">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpen(comm); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(comm.id); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          {expandedId === comm.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={isPendingTab ? 9 : 7} sx={{ py: 0, borderBottom: expandedId === comm.id ? undefined : 'none' }}>
          <Collapse in={expandedId === comm.id} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              {isPendingTab && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    icon={getPendingStatusIcon(comm.pending_status)}
                    label={`Estado: ${PENDING_STATUS_OPTIONS.find((o) => o.value === comm.pending_status)?.label || 'Abierto'}`}
                    color={PENDING_STATUS_COLORS[comm.pending_status] || 'default'}
                  />
                  {comm.assigned_to && (
                    <Chip label={`Asignado: ${comm.assigned_to}`} variant="outlined" />
                  )}
                  {comm.due_date && (
                    <Chip
                      label={`Plazo: ${fmtDateOnly(comm.due_date)}`}
                      color={isOverdue(comm) ? 'error' : 'default'}
                      variant="outlined"
                    />
                  )}
                  {comm.resolved_at && (
                    <Chip label={`Resuelto: ${fmtDate(comm.resolved_at)}`} color="success" variant="outlined" />
                  )}
                </Box>
              )}
              {comm.body && (
                <>
                  <Typography variant="subtitle2" gutterBottom>{isPendingTab ? 'Descripcion del tema' : 'Contenido'}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{comm.body}</Typography>
                </>
              )}
              {isPendingTab && comm.resolution && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Resolucion / Seguimiento</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{comm.resolution}</Typography>
                </>
              )}
              {comm.links?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Enlaces:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {comm.links.map((lnk) => (
                      <Chip
                        key={lnk.id}
                        icon={<OpenInNewIcon />}
                        label={lnk.label || lnk.url}
                        variant="outlined"
                        clickable
                        component="a"
                        href={lnk.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={subTab}
          onChange={(_, v) => { setSubTab(v); setExpandedId(null); setFilterCategory(''); setFilterDirection(''); setFilterPendingStatus(''); }}
          variant="fullWidth"
        >
          <Tab
            icon={<ForumIcon />}
            iconPosition="start"
            label={`Comunicaciones (${commCount})`}
          />
          <Tab
            icon={<AssignmentIcon />}
            iconPosition="start"
            label={`Temas pendientes (${openPendingCount}/${pendingCount})`}
          />
        </Tabs>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">
          {isPendingTab ? `Temas pendientes (${filtered.length})` : `Comunicaciones (${filtered.length})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {!isPendingTab && (
            <TextField
              select size="small" label="Filtrar tipo" value={filterDirection}
              onChange={(e) => setFilterDirection(e.target.value)} sx={{ minWidth: 130 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {DIRECTION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          )}
          {isPendingTab && (
            <TextField
              select size="small" label="Filtrar estado" value={filterPendingStatus}
              onChange={(e) => setFilterPendingStatus(e.target.value)} sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {PENDING_STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          )}
          <TextField
            select size="small" label="Filtrar categoria" value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)} sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {CATEGORY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Agrupar por" value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)} sx={{ minWidth: 150 }}
          >
            <MenuItem value="none">Sin agrupar</MenuItem>
            <MenuItem value="category">Categoria</MenuItem>
            {!isPendingTab && <MenuItem value="direction">Direccion</MenuItem>}
            {isPendingTab && <MenuItem value="status">Estado</MenuItem>}
            <MenuItem value="month">Mes</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            {isPendingTab ? 'Nuevo tema pendiente' : 'Nueva comunicacion'}
          </Button>
        </Box>
      </Box>

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {isPendingTab
              ? 'No hay temas pendientes. Pulsa "Nuevo tema pendiente" para crear uno.'
              : 'No hay comunicaciones registradas. Pulsa "Nueva comunicacion" para crear una.'}
          </Typography>
        </Paper>
      ) : (
        Object.entries(grouped).map(([groupLabel, items]) => (
          <Paper key={groupLabel} sx={{ mb: 2 }}>
            {groupLabel && (
              <Box sx={{ px: 2, py: 1, backgroundColor: '#c62828', color: '#fff', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" fontWeight="bold">{groupLabel} ({items.length})</Typography>
              </Box>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c62828' }}>
                    <TableCell sx={{ width: 40, color: '#fff' }} />
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'subject'}
                        direction={sortField === 'subject' ? sortDir : 'asc'}
                        onClick={() => handleSort('subject')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Asunto
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'contact_name'}
                        direction={sortField === 'contact_name' ? sortDir : 'asc'}
                        onClick={() => handleSort('contact_name')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        {isPendingTab ? 'Contacto / Responsable' : 'Contacto'}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
                    {isPendingTab && <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Prioridad</TableCell>}
                    {isPendingTab && (
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={sortField === 'due_date'}
                          direction={sortField === 'due_date' ? sortDir : 'asc'}
                          onClick={() => handleSort('due_date')}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          Plazo
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'communication_date'}
                        direction={sortField === 'communication_date' ? sortDir : 'asc'}
                        onClick={() => handleSort('communication_date')}
                        sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                      >
                        Fecha
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Enlaces</TableCell>
                    <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(renderRow)}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId
            ? (form.entry_type === 'pending' ? 'Editar tema pendiente' : 'Editar comunicacion')
            : (form.entry_type === 'pending' ? 'Nuevo tema pendiente' : 'Nueva comunicacion')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth select label="Tipo de entrada" value={form.entry_type}
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}>
                <MenuItem value="communication">Comunicacion</MenuItem>
                <MenuItem value="pending">Tema pendiente con seguimiento</MenuItem>
              </TextField>
            </Grid>
            {form.entry_type === 'communication' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Direccion" value={form.direction}
                  onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                  {DIRECTION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={form.entry_type === 'communication' ? 6 : 6}>
              <TextField fullWidth select label="Categoria" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            {form.entry_type === 'pending' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Estado" value={form.pending_status}
                  onChange={(e) => setForm({ ...form, pending_status: e.target.value })}>
                  {PENDING_STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label={form.entry_type === 'pending' ? 'Titulo del tema' : 'Asunto'} value={form.subject} required
                onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={form.entry_type === 'pending' ? 'Contacto externo' : 'Contacto'} value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={form.entry_type === 'pending' ? 'Fecha de apertura' : 'Fecha y hora'}
                type="datetime-local" value={form.communication_date}
                onChange={(e) => setForm({ ...form, communication_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            {form.entry_type === 'pending' && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="Prioridad" value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Fecha limite" type="date" value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Responsable interno" value={form.assigned_to}
                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={form.entry_type === 'pending' ? 4 : 6}
                label={form.entry_type === 'pending' ? 'Descripcion del tema' : 'Contenido'}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </Grid>
            {form.entry_type === 'pending' && (
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4}
                  label="Resolucion / Notas de seguimiento"
                  value={form.resolution}
                  onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                  helperText="Anotaciones sobre como se va resolviendo el tema" />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Enlaces a ficheros</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addLink}>Anadir enlace</Button>
              </Box>
              {form.links.map((link, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size="small" label="Nombre" value={link.label}
                    onChange={(e) => updateLink(idx, 'label', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Box sx={{ flex: 2 }}>
                    <FileDropField
                      value={link.url}
                      onChange={(val) => updateLink(idx, 'url', val)}
                      label="URL / Arrastra archivo"
                    />
                  </Box>
                  <IconButton size="small" color="error" onClick={() => removeLink(idx)}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.subject}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Eliminar entrada</DialogTitle>
        <DialogContent>
          <Typography>Se eliminara esta entrada y todos sus enlaces. Esta accion no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
